import { useMutation, type QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { STUDYGROUP_MEMBERSHIP_STATUS_MEMBER } from "@/constants";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import { fetchAllStudyGroupMemberIdsByStatus } from "@/lib/humhub-api/studyGroupMemberships";
import type {
  EventApiResponse,
  RecurrenceScope,
  ReminderData,
} from "@/lib/humhub-api/types";
import { eventKeys } from "@/lib/queryKeys/eventKeys";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

type ParticipantMessages = { success: string; error: string };
type ParticipantTitles = { success: string; error: string };
type ParticipantMutationArgs = {
  mutationFn: (
    eventId: number,
    userIds: number[],
    scope?: RecurrenceScope,
  ) => Promise<unknown>;
  messages: ParticipantMessages;
  titles: ParticipantTitles;
  invalidateEventData: (eventId: number, scope?: RecurrenceScope) => void;
  onDataRefresh?: () => void;
};
type ParticipantMutationHandler = Pick<
  ReturnType<typeof useParticipantsMutation>,
  "mutateAsync" | "isPending"
>;
type Translate = TFunction;

const useParticipantsMutation = ({
  mutationFn,
  messages,
  titles,
  invalidateEventData,
  onDataRefresh,
}: ParticipantMutationArgs) =>
  useMutation({
    mutationFn: async ({
      eventId,
      userIds,
      scope,
    }: {
      eventId: number;
      userIds: number[];
      scope?: RecurrenceScope;
    }) => mutationFn(eventId, userIds, scope),
    onSuccess: (_data, variables) => {
      invalidateEventData(variables.eventId, variables.scope);
      toast({
        title: titles.success,
        description: messages.success,
      });
      onDataRefresh?.();
    },
    onError: (error) => {
      console.error("Failed to update participants:", error);
      toast({
        title: titles.error,
        description: messages.error,
        variant: "error",
      });
    },
  });

const hasValidIds = (userIds?: number[]) =>
  Array.isArray(userIds) && userIds.length > 0;

type UseEventParticipantsActionsParams = {
  event: EventApiResponse;
  onDataRefresh?: () => void;
  queryClient: QueryClient;
  t: Translate;
  uniqueReminders: ReminderData[];
  updateAllParticipantsReminders: (
    eventId: number,
    reminders: ReminderData[],
    scope?: RecurrenceScope,
  ) => Promise<unknown>;
  propagateReminders: (
    eventId: number,
    scope: RecurrenceScope,
  ) => Promise<unknown>;
  deleteReminderByUser: (
    eventId: number,
    userId: number,
    scope?: RecurrenceScope,
  ) => Promise<unknown>;
  participantMessages: {
    accept: ParticipantMessages;
    decline: ParticipantMessages;
    titles: ParticipantTitles;
  };
};

export const useEventParticipantsActions = ({
  event,
  onDataRefresh,
  queryClient,
  t,
  uniqueReminders,
  updateAllParticipantsReminders,
  propagateReminders,
  deleteReminderByUser,
  participantMessages,
}: UseEventParticipantsActionsParams) => {
  const invalidateEventData = (eventId: number, scope?: RecurrenceScope) => {
    queryInvalidationHelpers.invalidateEventQueries(queryClient);
    if (scope && scope !== "single") {
      queryClient.invalidateQueries({
        predicate: (query) => eventKeys.isEventDetailRootKey(query.queryKey),
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          eventKeys.isEventParticipantsRootKey(query.queryKey),
      });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["eventDetail", eventId] });
    queryClient.invalidateQueries({ queryKey: ["eventParticipants", eventId] });
  };

  const acceptParticipantsMutation = useParticipantsMutation({
    mutationFn: eventsApi.acceptParticipants,
    messages: participantMessages.accept,
    titles: participantMessages.titles,
    invalidateEventData,
    onDataRefresh,
  });

  const declineParticipantsMutation = useParticipantsMutation({
    mutationFn: eventsApi.declineParticipants,
    messages: participantMessages.decline,
    titles: participantMessages.titles,
    invalidateEventData,
    onDataRefresh,
  });

  const canBulkAcceptParticipants = event?.container?.id != null;

  const fetchAllStudyGroupMemberIds = async (): Promise<number[]> => {
    if (!event?.container?.id) return [];
    return fetchAllStudyGroupMemberIdsByStatus(
      event.container.id,
      STUDYGROUP_MEMBERSHIP_STATUS_MEMBER,
    );
  };

  const runParticipantUpdate = async (
    userIds: number[],
    mutation: ParticipantMutationHandler,
    scope?: RecurrenceScope,
    options?: {
      onEmpty?: () => void;
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
    },
  ) => {
    if (!hasValidIds(userIds)) {
      options?.onEmpty?.();
      return;
    }
    try {
      await mutation.mutateAsync({ eventId: event.id, userIds, scope });
      options?.onSuccess?.();
    } catch (error) {
      options?.onError?.(error);
    }
  };

  const handleAcceptParticipants = async (
    userIds: number[],
    scope?: RecurrenceScope,
  ) => {
    if (
      !event?.id ||
      !canBulkAcceptParticipants ||
      acceptParticipantsMutation.isPending
    ) {
      return;
    }
    await runParticipantUpdate(userIds, acceptParticipantsMutation, scope, {
      onEmpty: () =>
        toast({
          title: t("eventManage.attendanceManagement"),
          description: t("eventManage.noStudyGroupMembers"),
        }),
      onSuccess: async () => {
        if (uniqueReminders.length === 0) return;
        const scopeToUse: RecurrenceScope | undefined =
          scope ?? (event?.recurrence ? "all" : undefined);
        try {
          await updateAllParticipantsReminders(
            event.id,
            uniqueReminders,
            scopeToUse,
          );
          await propagateReminders(event.id, scopeToUse ?? "single");
        } catch (error) {
          console.error(
            "Failed to update reminders for all participants:",
            error,
          );
        }
      },
    });
  };

  const handleDeclineParticipants = async (
    userIds: number[],
    scope?: RecurrenceScope,
  ) => {
    if (!event?.id || declineParticipantsMutation.isPending) return;
    await runParticipantUpdate(userIds, declineParticipantsMutation, scope, {
      onSuccess: async () => {
        if (uniqueReminders.length === 0) return;
        try {
          const scopeToUse: RecurrenceScope | undefined =
            scope ?? (event?.recurrence ? "all" : undefined);
          await Promise.all(
            userIds.map((userId) =>
              deleteReminderByUser(event.id, userId, scopeToUse),
            ),
          );
        } catch (error) {
          console.error(
            "Failed to delete reminders for declined participants:",
            error,
          );
        }
      },
    });
  };

  const handleAcceptAllParticipants = async () => {
    try {
      const memberIds = await fetchAllStudyGroupMemberIds();
      await handleAcceptParticipants(memberIds);
    } catch (error) {
      console.error("Failed to fetch study group members:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.acceptAllMembersFailed"),
        variant: "error",
      });
    }
  };

  return {
    canBulkAcceptParticipants,
    handleAcceptParticipants,
    handleDeclineParticipants,
    handleAcceptAllParticipants,
    acceptAllParticipantsLoading: acceptParticipantsMutation.isPending,
    declineParticipantsLoading: declineParticipantsMutation.isPending,
  };
};
